import { useState, useMemo, useEffect } from "react";
import { useRoute, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Loader2, Calendar as CalendarIcon, Download, FileText, Mail, Phone, ExternalLink } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface PressKitTalent {
  id: string;
  name: string;
  bookingSlug: string | null;
  heroPhotoUrl: string | null;
  longBio: string | null;
  bio: string | null;
  videoLinks: string[];
  audioLinks: string[];
  galleryImageUrls: string[];
  credits: Array<{ title: string; year?: string; role?: string; description?: string }>;
  pressQuotes: Array<{ quote: string; source?: string; sourceUrl?: string }>;
  logoUrls: string[];
  epkUrl: string | null;
  techRiderUrl: string | null;
  hospitalityRiderUrl: string | null;
  stagePlotUrl: string | null;
  faqItems: Array<{ question: string; answer: string }>;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contactCompany: string | null;
  pressKitDefaultBookingFormId: string | null;
}
interface PressKitBranding {
  primaryColor: string | null;
  workspaceName: string | null;
  logoUrl: string | null;
}
interface PressKitForm {
  id: string;
  slug: string | null;
  name: string;
  isDefault: boolean;
}
interface PressKitPayload {
  talent: PressKitTalent;
  branding: PressKitBranding;
  bookingForms: PressKitForm[];
  share: { recipientLabel?: string | null; expiresAt?: string | null } | null;
}
interface AvailabilityPayload {
  month: string;
  fullyBusyDates: string[];
}

function videoEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      return v ? `https://www.youtube.com/embed/${v}` : null;
    }
    if (u.hostname === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (u.hostname.includes("vimeo.com")) return `https://player.vimeo.com/video/${u.pathname.split("/").filter(Boolean)[0]}`;
    return null;
  } catch {
    return null;
  }
}

function audioEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("spotify.com")) {
      return `https://open.spotify.com/embed${u.pathname}`;
    }
    if (u.hostname.includes("soundcloud.com")) {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}`;
    }
    return null;
  } catch {
    return null;
  }
}

function PasswordGate({ token, onUnlocked }: { token: string; onUnlocked: () => void }) {
  const [password, setPassword] = useState("");
  const { toast } = useToast();
  const submit = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/public/artist/share/${encodeURIComponent(token)}/access`, { password }),
    onSuccess: () => onUnlocked(),
    onError: () => toast({ title: "Incorrect password", variant: "destructive" }),
  });
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/20 p-4">
      <Card className="max-w-sm w-full">
        <CardContent className="pt-6 space-y-4">
          <h1 className="text-lg font-semibold">Password required</h1>
          <p className="text-sm text-muted-foreground">This press kit is protected. Enter the password your sender shared.</p>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="input-share-password"
            onKeyDown={(e) => {
              if (e.key === "Enter") submit.mutate();
            }}
          />
          <Button className="w-full" onClick={() => submit.mutate()} disabled={submit.isPending} data-testid="button-share-unlock">
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function AvailabilityMonth({ slug }: { slug: string }) {
  const today = new Date();
  const [month, setMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const { data, isLoading } = useQuery<AvailabilityPayload>({
    queryKey: ["/api/public/artist", slug, "availability", month],
    queryFn: async () => {
      const res = await fetch(`/api/public/artist/${encodeURIComponent(slug)}/availability?month=${month}`);
      if (!res.ok) throw new Error("Failed to load availability");
      return res.json();
    },
  });
  const busy = useMemo(() => new Set(data?.fullyBusyDates ?? []), [data]);
  const [year, mo] = month.split("-").map(Number);
  const daysInMonth = new Date(year, mo, 0).getDate();
  const firstDow = new Date(year, mo - 1, 1).getDay();
  const cells: Array<{ d: number | null; key: string }> = [];
  for (let i = 0; i < firstDow; i++) cells.push({ d: null, key: `e${i}` });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ d, key: String(d) });
  const shift = (delta: number) => {
    const next = new Date(year, mo - 1 + delta, 1);
    setMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`);
  };
  return (
    <div className="space-y-2" data-testid="press-availability">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="button-availability-prev">‹</Button>
        <div className="font-medium">{new Date(year, mo - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" })}</div>
        <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="button-availability-next">›</Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={`h${i}`} className="text-center text-muted-foreground">{d}</div>
        ))}
        {cells.map((c) => {
          if (c.d === null) return <div key={c.key} />;
          const dateStr = `${year}-${String(mo).padStart(2, "0")}-${String(c.d).padStart(2, "0")}`;
          const isBusy = busy.has(dateStr);
          return (
            <div
              key={c.key}
              className={`h-9 rounded text-center pt-1.5 ${isBusy ? "bg-muted text-muted-foreground line-through" : "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"}`}
              data-testid={`day-${dateStr}`}
            >
              {c.d}
            </div>
          );
        })}
      </div>
      {isLoading && <div className="text-xs text-muted-foreground">Loading availability…</div>}
      <div className="text-xs text-muted-foreground">Greyed dates are typically unavailable. Contact for exact holds.</div>
    </div>
  );
}

function PressKitView({ payload, slug, shareToken }: { payload: PressKitPayload; slug: string | null; shareToken: string | null }) {
  const { talent, branding, bookingForms } = payload;
  const primary = branding.primaryColor || "#1d4ed8";
  const defaultForm = bookingForms.find((f) => f.id === talent.pressKitDefaultBookingFormId) || bookingForms.find((f) => f.isDefault) || bookingForms[0];
  const bookHref = talent.bookingSlug
    ? defaultForm?.slug
      ? `/book/${talent.bookingSlug}/${defaultForm.slug}`
      : `/book/${talent.bookingSlug}`
    : null;
  const pdfHref = talent.bookingSlug ? `/api/artist/${encodeURIComponent(talent.bookingSlug)}/pdf` : null;
  const heroBg = talent.heroPhotoUrl ? { backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.55)), url(${talent.heroPhotoUrl})` } : { background: primary };
  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="bg-cover bg-center text-white" style={heroBg} data-testid="press-hero">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="flex items-center gap-3 mb-3 text-sm opacity-90">
            {branding.logoUrl && <img src={branding.logoUrl} alt="" className="h-6" />}
            {branding.workspaceName && <span>{branding.workspaceName}</span>}
          </div>
          <h1 className="text-5xl font-bold mb-3" data-testid="text-talent-name">{talent.name}</h1>
          <div className="flex flex-wrap gap-2 mt-4">
            {bookHref && (
              <Link href={bookHref}>
                <Button size="lg" style={{ backgroundColor: primary }} data-testid="button-book-now">Request booking</Button>
              </Link>
            )}
            {pdfHref && (
              <a href={pdfHref} target="_blank" rel="noreferrer">
                <Button size="lg" variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white/20" data-testid="button-download-pdf">
                  <Download className="h-4 w-4 mr-1" /> One-sheet PDF
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {(talent.longBio || talent.bio) && (
            <section data-testid="section-bio">
              <h2 className="text-xl font-semibold mb-2">About</h2>
              <p className="whitespace-pre-line text-muted-foreground">{talent.longBio || talent.bio}</p>
            </section>
          )}

          {talent.videoLinks.length > 0 && (
            <section data-testid="section-videos">
              <h2 className="text-xl font-semibold mb-3">Videos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {talent.videoLinks.map((url) => {
                  const embed = videoEmbedUrl(url);
                  return embed ? (
                    <div key={url} className="aspect-video rounded overflow-hidden bg-black">
                      <iframe src={embed} className="w-full h-full" allowFullScreen title="Video" />
                    </div>
                  ) : (
                    <a key={url} href={url} target="_blank" rel="noreferrer" className="text-sm text-primary underline">
                      {url}
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {talent.audioLinks.length > 0 && (
            <section data-testid="section-audio">
              <h2 className="text-xl font-semibold mb-3">Audio</h2>
              <div className="space-y-3">
                {talent.audioLinks.map((url) => {
                  const embed = audioEmbedUrl(url);
                  return embed ? (
                    <iframe key={url} src={embed} className="w-full h-20" title="Audio" />
                  ) : (
                    <a key={url} href={url} target="_blank" rel="noreferrer" className="text-sm text-primary underline">
                      {url}
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {talent.galleryImageUrls.length > 0 && (
            <section data-testid="section-gallery">
              <h2 className="text-xl font-semibold mb-3">Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {talent.galleryImageUrls.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt="" className="w-full h-40 object-cover rounded" loading="lazy" />
                  </a>
                ))}
              </div>
            </section>
          )}

          {talent.credits.length > 0 && (
            <section data-testid="section-credits">
              <h2 className="text-xl font-semibold mb-3">Credits</h2>
              <ul className="space-y-2">
                {talent.credits.map((c, i) => (
                  <li key={i} className="text-sm">
                    <strong>{c.title}</strong>{c.year ? ` (${c.year})` : ""}{c.role ? ` — ${c.role}` : ""}
                    {c.description && <div className="text-muted-foreground">{c.description}</div>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {talent.pressQuotes.length > 0 && (
            <section data-testid="section-quotes">
              <h2 className="text-xl font-semibold mb-3">Press</h2>
              <div className="space-y-3">
                {talent.pressQuotes.map((q, i) => (
                  <blockquote key={i} className="border-l-4 pl-4 italic" style={{ borderColor: primary }}>
                    <p>“{q.quote}”</p>
                    {q.source && (
                      <footer className="text-sm text-muted-foreground mt-1 not-italic">
                        — {q.sourceUrl ? <a href={q.sourceUrl} target="_blank" rel="noreferrer" className="underline">{q.source}</a> : q.source}
                      </footer>
                    )}
                  </blockquote>
                ))}
              </div>
            </section>
          )}

          {talent.logoUrls.length > 0 && (
            <section data-testid="section-logos">
              <h2 className="text-xl font-semibold mb-3">Featured / brands</h2>
              <div className="flex flex-wrap items-center gap-6">
                {talent.logoUrls.map((url) => (
                  <img key={url} src={url} alt="" className="h-10 grayscale opacity-80 hover:opacity-100 hover:grayscale-0 transition" loading="lazy" />
                ))}
              </div>
            </section>
          )}

          {talent.faqItems.length > 0 && (
            <section data-testid="section-faq">
              <h2 className="text-xl font-semibold mb-3">FAQ</h2>
              <Accordion type="single" collapsible>
                {talent.faqItems.map((q, i) => (
                  <AccordionItem key={i} value={`item-${i}`}>
                    <AccordionTrigger>{q.question}</AccordionTrigger>
                    <AccordionContent>{q.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}
        </div>

        <aside className="space-y-6">
          <Card data-testid="card-contact">
            <CardContent className="pt-6 space-y-2">
              <h3 className="font-semibold">Contact</h3>
              {talent.contactName && (
                <div className="text-sm">
                  <strong>{talent.contactName}</strong>
                  {talent.contactCompany ? <span className="text-muted-foreground"> — {talent.contactCompany}</span> : null}
                </div>
              )}
              {talent.contactEmail && (
                <a href={`mailto:${talent.contactEmail}`} className="flex items-center text-sm text-primary">
                  <Mail className="h-4 w-4 mr-2" /> {talent.contactEmail}
                </a>
              )}
              {talent.contactPhone && (
                <a href={`tel:${talent.contactPhone}`} className="flex items-center text-sm text-primary">
                  <Phone className="h-4 w-4 mr-2" /> {talent.contactPhone}
                </a>
              )}
              {bookHref && (
                <Link href={bookHref}>
                  <Button className="w-full mt-3" style={{ backgroundColor: primary }} data-testid="button-book-side">Request booking</Button>
                </Link>
              )}
            </CardContent>
          </Card>

          {(talent.epkUrl || talent.techRiderUrl || talent.hospitalityRiderUrl || talent.stagePlotUrl) && (
            <Card data-testid="card-docs">
              <CardContent className="pt-6 space-y-2">
                <h3 className="font-semibold">Documents</h3>
                {[
                  { label: "EPK", url: talent.epkUrl, key: "epk" },
                  { label: "Tech rider", url: talent.techRiderUrl, key: "tech" },
                  { label: "Hospitality rider", url: talent.hospitalityRiderUrl, key: "hosp" },
                  { label: "Stage plot", url: talent.stagePlotUrl, key: "stage" },
                ]
                  .filter((d) => d.url)
                  .map((d) => (
                    <a key={d.key} href={d.url ?? "#"} target="_blank" rel="noreferrer" className="flex items-center text-sm text-primary" data-testid={`link-doc-${d.key}`}>
                      <FileText className="h-4 w-4 mr-2" /> {d.label} <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  ))}
              </CardContent>
            </Card>
          )}

          {slug && (
            <Card data-testid="card-availability">
              <CardContent className="pt-6 space-y-2">
                <h3 className="font-semibold flex items-center"><CalendarIcon className="h-4 w-4 mr-2" /> General availability</h3>
                <AvailabilityMonth slug={slug} />
              </CardContent>
            </Card>
          )}

          {payload.share?.recipientLabel && (
            <div className="text-xs text-muted-foreground">
              Shared with: {payload.share.recipientLabel}
              {payload.share.expiresAt && (
                <> · expires {new Date(payload.share.expiresAt).toLocaleDateString()}</>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default function PublicArtistPage() {
  const [matchSlug, slugParams] = useRoute("/artist/:slug");
  const [matchShare, shareParams] = useRoute("/artist/share/:token");
  const slug = matchSlug ? slugParams?.slug ?? null : null;
  const token = matchShare ? shareParams?.token ?? null : null;
  const [unlocked, setUnlocked] = useState(0);

  const slugQuery = useQuery<PressKitPayload>({
    queryKey: ["/api/public/artist", slug],
    enabled: !!slug,
    queryFn: async () => {
      const res = await fetch(`/api/public/artist/${encodeURIComponent(slug!)}`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      return res.json();
    },
  });

  const shareQuery = useQuery<PressKitPayload | { passwordRequired: true }>({
    queryKey: ["/api/public/artist/share", token, unlocked],
    enabled: !!token,
    queryFn: async () => {
      const res = await fetch(`/api/public/artist/share/${encodeURIComponent(token!)}`);
      if (res.status === 401) return { passwordRequired: true };
      if (!res.ok) throw new Error(`Status ${res.status}`);
      return res.json();
    },
    retry: false,
  });

  useEffect(() => {
    if (slugQuery.data?.talent?.name) document.title = `${slugQuery.data.talent.name} — Press kit`;
    const sd = shareQuery.data as PressKitPayload | { passwordRequired: true } | undefined;
    if (sd && "talent" in sd) document.title = `${sd.talent.name} — Press kit`;
  }, [slugQuery.data, shareQuery.data]);

  if (token) {
    if (shareQuery.isLoading) {
      return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
    }
    const sd = shareQuery.data;
    if (sd && "passwordRequired" in sd) return <PasswordGate token={token} onUnlocked={() => setUnlocked((u) => u + 1)} />;
    if (shareQuery.error || !sd) {
      return (
        <div className="min-h-screen flex items-center justify-center text-center px-6">
          <div>
            <h1 className="text-2xl font-semibold mb-2">Link unavailable</h1>
            <p className="text-muted-foreground">This share link has expired or been revoked.</p>
          </div>
        </div>
      );
    }
    // Derive slug from the share payload so the availability calendar +
    // /book CTA still work on share-token pages (the slug is non-secret —
    // only the password/expiry gate matters for limiting access).
    const sharePayload = sd as PressKitPayload;
    return <PressKitView payload={sharePayload} slug={sharePayload.talent.bookingSlug ?? null} shareToken={token} />;
  }

  if (!slug) return null;
  if (slugQuery.isLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (slugQuery.error || !slugQuery.data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-6">
        <div>
          <h1 className="text-2xl font-semibold mb-2">Press kit not found</h1>
          <p className="text-muted-foreground">This artist hasn't published a public page yet.</p>
        </div>
      </div>
    );
  }
  return <PressKitView payload={slugQuery.data} slug={slug} shareToken={null} />;
}
